/* eslint-disable react/jsx-props-no-spreading */
import React, { Suspense } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { Helmet } from 'react-helmet';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, Typography, Stack, Grid, Tabs, Tab, TextField } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { useRecoilState } from 'recoil';
import ErrorFallback from 'components/error-fallback';
import Empty from 'components/empty';
import Btn from 'components/button';
import { useHistory, useLocation } from 'react-router-dom';
import { scope } from 'utils/scope';
import { getUserScopes } from 'utils';
import { catalogs } from 'store';
import { commonStyles as style } from 'assets/style';
import styles, { ServicePage, CssTextField } from 'assets/style/styles';
import { filterCatalogs, findNested, getModifiedCatalogs } from './utils';

const CatalogService = React.lazy(() => import('pages/services/CatalogService'));
const ServiceFilter = React.lazy(() => import('pages/services/filter'));

function Services() {
    const history = useHistory();
    const location = useLocation();
    const [searchText, setSearchText] = React.useState('');
    const [tabSearchText, setTabSearchText] = React.useState('');
    const [catlogsData, setCatalogsData] = React.useState([]);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [tabValue, setTabValue] = React.useState(0);
    const [showCommonServices, setShowCommonServices] = React.useState(false);
    const refToTop = React.useRef();
    const excludedFromAzureTab = ['AWS', 'Private Cloud'];

    React.useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const servicesParam = queryParams.get('services');
        const tabMap = { aws: 0, azure: 1, 'private-cloud': 2, 'access-management': 3 };

        if (servicesParam in tabMap) {
            setTabValue(tabMap[servicesParam]);
        } else if (servicesParam) {
            const newQueryParams = new URLSearchParams(location.search);
            newQueryParams.set('services', 'aws');
            history.replace({
                pathname: location.pathname,
                search: newQueryParams.toString(),
            });
            setTabValue(0);
        } else {
            setTabValue(0);
        }
    }, [location.search, history]);

    const filterByCloudProvider = (catalogsArray) => {
        const filters = {
            0: (c) => c.cloudProvider === 'AWS',
            1: (c) => !excludedFromAzureTab.includes(c.cloudProvider) && !c.isAccessManagementCatalog,
            2: (c) => c.cloudProvider === 'Private Cloud',
            3: (c) => c.cloudProvider === 'Cloud Marketplace' && c.isAccessManagementCatalog === true,
        };
        return filters[tabValue] ? catalogsArray.filter(filters[tabValue]) : catalogsArray;
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setTabSearchText('');
        if (showCommonServices) {
            setShowCommonServices(false);
            setSearchText('');
        }

        const queryParams = new URLSearchParams(location.search);
        const serviceNames = ['aws', 'azure', 'private-cloud', 'access-management'];
        queryParams.set('services', serviceNames[newValue]);

        history.push({
            pathname: location.pathname,
            search: queryParams.toString(),
        });
    };

    React.useEffect(() => {
        let filteredCatalogs = cmpCatalogs;

        if (searchText !== '') {
            const globalFind = findNested(cmpCatalogs, searchText);
            filteredCatalogs = [...new Set(globalFind)];
        }

        if (!showCommonServices) {
            filteredCatalogs = filterByCloudProvider(filteredCatalogs);
        }

        if (tabSearchText !== '') {
            const tabFind = findNested(filteredCatalogs, tabSearchText);
            filteredCatalogs = [...new Set(tabFind)];
        }

        setCatalogsData(getModifiedCatalogs(filteredCatalogs));
    }, [cmpCatalogs, tabValue, searchText, tabSearchText, showCommonServices]);

    const findSearched = (val) => {
        if (val === '') {
            setShowCommonServices(false);
            let filteredCatalogs = filterByCloudProvider(cmpCatalogs);
            if (tabSearchText !== '') {
                const tabFind = findNested(filteredCatalogs, tabSearchText);
                filteredCatalogs = [...new Set(tabFind)];
            }
            setCatalogsData(getModifiedCatalogs(filteredCatalogs));
            return;
        }
        const uniqueResults = [...new Set(findNested(cmpCatalogs, val))];
        const providerGroups = [
            { tab: 0, results: uniqueResults.filter((c) => c.cloudProvider === 'AWS') },
            { tab: 1, results: uniqueResults.filter((c) => !excludedFromAzureTab.includes(c.cloudProvider) && !c.isAccessManagementCatalog) },
            { tab: 2, results: uniqueResults.filter((c) => c.cloudProvider === 'Private Cloud') },
            { tab: 3, results: uniqueResults.filter((c) => c.cloudProvider === 'Cloud Marketplace' && c.isAccessManagementCatalog === true) },
        ];

        const nonEmptyGroups = providerGroups.filter((g) => g.results.length > 0);

        if (nonEmptyGroups.length > 1) {
            setShowCommonServices(true);
            setCatalogsData(getModifiedCatalogs(uniqueResults));
        } else if (nonEmptyGroups.length === 1) {
            setShowCommonServices(false);
            if (tabValue !== nonEmptyGroups[0].tab) {
                setTabValue(nonEmptyGroups[0].tab);
            }
            setCatalogsData(getModifiedCatalogs(nonEmptyGroups[0].results));
        } else {
            setShowCommonServices(false);
            setCatalogsData([]);
        }
    };

    const findTabSearched = (val) => {
        let filteredCatalogs = cmpCatalogs;

        if (searchText !== '') {
            const globalFind = findNested(cmpCatalogs, searchText);
            filteredCatalogs = [...new Set(globalFind)];
        }

        if (!showCommonServices) {
            filteredCatalogs = filterByCloudProvider(filteredCatalogs);
        }

        if (val !== '') {
            const tabFind = findNested(filteredCatalogs, val);
            filteredCatalogs = [...new Set(tabFind)];
        }

        setCatalogsData(getModifiedCatalogs(filteredCatalogs));
    };
    React.useEffect(() => {
        if (searchText === '') {
            setShowCommonServices(false);
            let filteredCatalogs = filterByCloudProvider(cmpCatalogs);
            if (tabSearchText !== '') {
                const tabFind = findNested(filteredCatalogs, tabSearchText);
                filteredCatalogs = [...new Set(tabFind)];
            }
            setCatalogsData(getModifiedCatalogs(filteredCatalogs));
        } else {
            findSearched(searchText);
        }
    }, [searchText, tabValue, cmpCatalogs]);

    React.useEffect(() => {
        findTabSearched(tabSearchText);
    }, [tabSearchText]);

    const filterData = (catalog) => {
        let filteredCatalogs = cmpCatalogs;

        if (searchText !== '') {
            const globalFind = findNested(cmpCatalogs, searchText);
            filteredCatalogs = [...new Set(globalFind)];
        }

        filteredCatalogs = filterCatalogs(filteredCatalogs, catalog);

        if (!showCommonServices) {
            filteredCatalogs = filterByCloudProvider(filteredCatalogs);
        }

        if (tabSearchText !== '') {
            const tabFind = findNested(filteredCatalogs, tabSearchText);
            filteredCatalogs = [...new Set(tabFind)];
        }

        setCatalogsData(getModifiedCatalogs(filteredCatalogs));
    };

    React.useEffect(() => {
        if (refToTop.current && typeof refToTop.current.scrollIntoView === 'function') {
            refToTop.current.scrollIntoView();
        }
    }, []);

    return (
        <>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Helmet>
                    <title>Services - DevX Infra</title>
                </Helmet>
                <Suspense
                    fallback={
                        <Stack sx={styles.loadingStack}>
                            <Empty color="#000000" isLoading />
                        </Stack>
                    }
                >
                    <Stack spacing={2} sx={styles.mainstack} ref={refToTop} data-testid="services">
                        {cmpCatalogs?.length > 0 && (
                            <>
                                <Box sx={styles?.headerMainWrapper} className="services-offered-section">
                                    <Box sx={styles.headercontainer} spacing={1} className="contentContainer">
                                        <Typography variant="h6" sx={styles.headingTypo}>
                                            {' '}
                                            Services
                                        </Typography>
                                        <Box sx={styles.actionContainer}>
                                            <Box sx={styles.searchbox} data-testid="search-box">
                                                <CssTextField
                                                    fullWidth
                                                    autoComplete="off"
                                                    label="Search All Services"
                                                    size="small"
                                                    variant="outlined"
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    value={searchText}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <>
                                                                {searchText && (
                                                                    <IconButton
                                                                        aria-label="Search "
                                                                        sx={styles.clearicon}
                                                                        onClick={() => {
                                                                            setSearchText('');
                                                                        }}
                                                                    >
                                                                        <ClearIcon />
                                                                    </IconButton>
                                                                )}
                                                                <>
                                                                    <IconButton onClick={() => findSearched(searchText)} data-testid="find-search-icon">
                                                                        <SearchIcon sx={styles.searchicon} />
                                                                    </IconButton>
                                                                </>
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                            <ServiceFilter handleFilter={filterData} />
                                            {getUserScopes()?.includes(scope[2]) && (
                                                <Box sx={{ ml: 1 }}>
                                                    <Btn
                                                        size="medium"
                                                        onClick={() => history.push('/catalogs')}
                                                        variant="contained"
                                                        sx={{ ...style.primaryMediumButton, height: '38px' }}
                                                    >
                                                        Edit Catalog
                                                    </Btn>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        width: '100%',
                                        mb: 2,
                                        // ml: 8,
                                        // mr: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderBottom: showCommonServices ? 'none' : '1px solid rgb(215 229 248)',
                                    }}
                                    className="contentContainer"
                                >
                                    {!showCommonServices && (
                                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="service tabs" sx={ServicePage.tabsContainer}>
                                            <Tab label="AWS Services" className="aws-service-tab" />
                                            <Tab label="Azure Services" className="azure-service-tab" />
                                            <Tab label="Private Cloud Services" className="private-cloud-service-tab" />
                                            <Tab label="Access Management" className="access-management-tab" />
                                        </Tabs>
                                    )}
                                </Box>

                                {showCommonServices && (
                                    <Box
                                        sx={{
                                            mb: 3,
                                            backgroundColor: '#ffffff',
                                            border: '1px solid rgb(215 229 248)',
                                            borderRadius: '5px',
                                            padding: '10px 5px',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                        className="contentContainer"
                                    >
                                        <Box
                                            sx={{
                                                width: '6px',
                                                // height: '24px',
                                                backgroundColor: '#2196f3',
                                                borderRadius: '3px',
                                                mr: 2,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#1976d2',
                                                fontWeight: 500,
                                            }}
                                        >
                                            Showing common services available across multiple cloud platforms. These services can be provisioned in either cloud
                                            platform.
                                        </Typography>
                                    </Box>
                                )}

                                <Box className="contentContainer">
                                    {!showCommonServices && (
                                        <Box sx={{ mb: 3 }}>
                                            <TextField
                                                autoComplete="off"
                                                placeholder={`Search in ${['AWS', 'Azure', 'Private Cloud', 'Access Management'][tabValue]} Services`}
                                                size="small"
                                                variant="outlined"
                                                onChange={(e) => setTabSearchText(e.target.value)}
                                                value={tabSearchText}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '5px',
                                                        backgroundColor: '#FFFFFF',
                                                        border: '1px solid #e0e0e0',
                                                        paddingLeft: '12px',
                                                        '&:hover': {
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid #d0d0d0',
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor: '#ffffff',
                                                            border: '1px solid rgb(215 229 248)',
                                                            boxShadow: 'none',
                                                        },
                                                        '& fieldset': {
                                                            border: 'none',
                                                            borderRadius: '5px',
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderRadius: '5px',
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        paddingLeft: '8px',
                                                    },
                                                    '& .MuiInputBase-root': {
                                                        borderRadius: '5px',
                                                    },
                                                }}
                                                InputProps={{
                                                    startAdornment: <SearchIcon sx={{ color: 'rgb(0, 71, 186)', mr: 1, fontSize: '15px' }} />,
                                                    endAdornment: (
                                                        <>
                                                            {tabSearchText && (
                                                                <IconButton
                                                                    aria-label="Clear tab search"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setTabSearchText('');
                                                                    }}
                                                                    sx={{
                                                                        color: '#666',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                                        },
                                                                    }}
                                                                >
                                                                    <ClearIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Grid
                                        container
                                        spacing={2}
                                        sx={{
                                            ...styles.gridcontain,
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                            '& .MuiGrid-item': {
                                                display: 'flex',
                                                justifyContent: 'center',
                                            },
                                        }}
                                        className="catalog-service-items-container"
                                    >
                                        {catlogsData?.length <= 0 && (
                                            <Grid
                                                item
                                                xs={12}
                                                key="empty-grid"
                                                sx={{
                                                    gridColumn: '1 / -1',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    minHeight: '50vh',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Empty notFound color="#000000" msg="No Services Found" />
                                            </Grid>
                                        )}
                                        {catlogsData?.length > 0 &&
                                            catlogsData
                                                ?.filter((service) => service?.isCatalogToDisplay || service?.isAccessManagementCatalog)
                                                ?.map((service, idx) => (
                                                    <>
                                                        <Grid
                                                            item
                                                            key={service.catalogDefinitionId}
                                                            sx={styles.griditemcontain}
                                                            className="catalog-service-item"
                                                        >
                                                            <CatalogService service={service} shownumber={false} idx={idx + 1} color="#CCCCCC" />
                                                        </Grid>
                                                    </>
                                                ))}
                                    </Grid>
                                </Box>
                            </>
                        )}
                    </Stack>
                </Suspense>
            </ErrorBoundary>
        </>
    );
}

export default Services;
